import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { DocumentsTab } from 'components/DocumentsTabs/DocumentsTabs';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'components/Table/Table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CONSUMPTION_PERIODS,
    CONSUMPTION_PERIOD_CLOSE,
    CONSUMPTION_PERIOD_GENERATE_INVOICE,
} from 'services/consumption';

const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(num);
};

const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
        case 'OPEN':
            return 'outline';
        case 'CLOSED':
            return 'secondary';
        case 'INVOICED':
            return 'default';
        default:
            return 'outline';
    }
};

export const ConsumptionPeriodsList = () => {
    const navigate = useNavigate();
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const { data, loading, refetch } = useQuery(CONSUMPTION_PERIODS, {
        variables: {
            first: 100,
            year: selectedYear,
            month: selectedMonth,
            status: selectedStatus,
        },
    });

    const [closePeriod, { loading: closing }] = useMutation(CONSUMPTION_PERIOD_CLOSE, {
        onCompleted: () => refetch(),
    });

    const [generateInvoice, { loading: generating }] = useMutation(CONSUMPTION_PERIOD_GENERATE_INVOICE, {
        onCompleted: () => refetch(),
    });

    const periods = data?.consumptionPeriods?.edges?.map((e: { node: unknown }) => e.node) || [];

    const handleClose = async (periodId: string) => {
        if (window.confirm('Are you sure you want to close this period? This cannot be undone.')) {
            await closePeriod({ variables: { periodId } });
        }
    };

    const handleGenerateInvoice = async (periodId: string) => {
        await generateInvoice({ variables: { periodId } });
    };

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    return (
        <div>
            <Breadcrumbs crumbs={['Dashboard', 'Consumption Periods']} />
            <DocumentsTab />
            <PageTitle>Consumption Periods</PageTitle>

            <Card className="p-4 mb-4">
                <div className="flex gap-4 flex-wrap items-end">
                    <div>
                        <label className="block text-sm font-medium mb-1">Year</label>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="border rounded px-3 py-2"
                        >
                            {years.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Month</label>
                        <select
                            value={selectedMonth || ''}
                            onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : null)}
                            className="border rounded px-3 py-2"
                        >
                            <option value="">All Months</option>
                            {months.map((month) => (
                                <option key={month.value} value={month.value}>
                                    {month.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Status</label>
                        <select
                            value={selectedStatus || ''}
                            onChange={(e) => setSelectedStatus(e.target.value || null)}
                            className="border rounded px-3 py-2"
                        >
                            <option value="">All Statuses</option>
                            <option value="OPEN">Open</option>
                            <option value="CLOSED">Closed</option>
                            <option value="INVOICED">Invoiced</option>
                        </select>
                    </div>
                    <SearchFilter placeholder="Search by name..." />
                </div>
            </Card>

            <Table className="border">
                <TableHeader>
                    <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead>Member/Company</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Entries</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={8} className="text-center py-8">
                                Loading...
                            </TableCell>
                        </TableRow>
                    )}
                    {!loading && periods.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                                No consumption periods found
                            </TableCell>
                        </TableRow>
                    )}
                    {periods.map((period: {
                        id: string;
                        year: number;
                        month: number;
                        billingEntityName: string;
                        billingEntityType: string;
                        entryCount: number;
                        calculatedTotal: number;
                        status: string;
                        invoice?: {
                            id: string;
                            invoiceId: string;
                            status: string;
                            document?: { url: string };
                        };
                    }) => (
                        <TableRow key={period.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium">
                                {period.year}/{String(period.month).padStart(2, '0')}
                            </TableCell>
                            <TableCell>{period.billingEntityName || '-'}</TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {period.billingEntityType === 'company' ? 'Company' : 'Member'}
                                </Badge>
                            </TableCell>
                            <TableCell>{period.entryCount}</TableCell>
                            <TableCell>{formatCurrency(period.calculatedTotal)}</TableCell>
                            <TableCell>
                                <Badge variant={getStatusBadgeVariant(period.status)}>
                                    {period.status}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {period.invoice ? (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm">{period.invoice.invoiceId}</span>
                                        {period.invoice.document?.url && (
                                            <a
                                                href={period.invoice.document.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline text-sm"
                                            >
                                                PDF
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <span className="text-gray-400">-</span>
                                )}
                            </TableCell>
                            <TableCell>
                                <div className="flex gap-2">
                                    {period.status === 'OPEN' && period.entryCount > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleClose(period.id)}
                                            disabled={closing}
                                        >
                                            Close
                                        </Button>
                                    )}
                                    {period.status === 'CLOSED' && !period.invoice && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleGenerateInvoice(period.id)}
                                            disabled={generating}
                                        >
                                            Generate Invoice
                                        </Button>
                                    )}
                                    {period.invoice && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate(`/invoices/${period.invoice?.id}`)}
                                        >
                                            View Invoice
                                        </Button>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
